import './JobPostingModal.scss';

import { Button, Form, Modal } from "react-bootstrap";
import { useEffect } from 'react';
import LocationInput from '../../../components/Form/LocationInput';
import { useForm } from '../../../hooks/Form';
import { api } from '../../../utils/api';

function formatDateForInput(dateString) {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d)) return "";
    return d.toISOString().split("T")[0]; // keeps only YYYY-MM-DD
}

function createInitialValues(job = null) {
    return {
        title: job?.title ?? "",
        description: job?.description ?? "",
        status: job?.status ?? "active",
        location: job?.location ?? "",
        latitude: job?.latitude ?? "",
        longitude: job?.longitude ?? "",
        author: job?.author ?? "",
        budget: job?.budget ?? "",
        facebook_group: job?.facebook_group ?? "",
        genre: job?.genre ?? "",
        post_url: job?.post_url ?? "",
        tags: job?.tags ?? "",
        owner_reached: job?.owner_reached ?? false,
        posted_at: formatDateForInput(job?.posted_at),
        expires_at: formatDateForInput(job?.expires_at),
    };
}

export default function JobPostingModal({ show, handleClose, job, setJobs }) {
    const { values, setValues, handleChange } = useForm({ initialValues: createInitialValues(job) });

    const clearLocationValues = () => {
        setValues((oldValues) => ({
            ...oldValues,
            location: null,
            latitude: null,
            longitude: null,
        }));
    };

    const handleLocationInputClear = () => {
        clearLocationValues();
    };

    const handleLocationPlaceChange = (place) => {
        if (!place || !place.address || !place.latitude || !place.longitude) {
            return clearLocationValues();
        }

        setValues((oldValues) => ({
            ...oldValues,
            location: place.address,
            latitude: place.latitude,
            longitude: place.longitude,
        }));
    };

    const handleSave = async () => {
        const payload = {};

        // Convert empty strings to null and remove undefined
        Object.entries(values).forEach(([key, value]) => {
            if (value !== "" && value !== undefined) {
                payload[key] = value;
            } else {
                payload[key] = null;
            }
        });

        if (job && job.id) {
            setJobs(prevJobs => prevJobs.map(j => j.id === job.id ? ({ ...j, ...payload }) : j));

            api.put(`/job_postings/${job.id}`, payload);

        } else {
            const { data: newJob } = await api.post(`/job_postings`, payload);

            setJobs(prevJobs => [newJob, ...prevJobs]);
        }

        handleClose();
    };

    const handleDelete = async () => {
        setJobs(prevJobs => prevJobs.filter(j => j.id !== job.id));

        api.delete(`/job_postings/${job.id}`);

        handleClose();
    };

    useEffect(() => {
        setValues(createInitialValues(job));
    }, [setValues, job]);

    return (
        <Modal className="__job-posting-modal" show={ show } onHide={ handleClose } centered>
            <Modal.Header closeButton>
                <Modal.Title>{ job ? "Edit Job Posting" : "Create Job Posting" }</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                            name="status"
                            value={ values.status ?? "active" }
                            onChange={ handleChange }
                        >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Author</Form.Label>
                        <Form.Control
                            name="author"
                            value={ values.author ?? "" }
                            onChange={ handleChange }
                            placeholder="Enter author"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                            name="title"
                            value={ values.title ?? "" }
                            onChange={ handleChange }
                            placeholder="Enter job title"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={ 3 }
                            name="description"
                            value={ values.description ?? "" }
                            onChange={ handleChange }
                            placeholder="Enter job description"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Location</Form.Label>
                        <LocationInput
                            name="location"
                            placeholder="Enter location"
                            value={ values.location ?? "" }
                            onInputClear={ handleLocationInputClear }
                            onPlaceChange={ handleLocationPlaceChange }
                        />
                    </Form.Group>


                    <Form.Group className="mb-3">
                        <Form.Label>Expires At</Form.Label>
                        <Form.Control
                            type="date"
                            name="expires_at"
                            value={ values.expires_at ?? "" }
                            onChange={ handleChange }
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Posted At</Form.Label>
                        <Form.Control
                            type="date"
                            name="posted_at"
                            value={ values.posted_at ?? "" }
                            onChange={ handleChange }
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Budget</Form.Label>
                        <Form.Control
                            type="number"
                            name="budget"
                            value={ values.budget ?? "" }
                            onChange={ handleChange }
                            placeholder="Enter budget"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Facebook Group</Form.Label>
                        <Form.Control
                            name="facebook_group"
                            value={ values.facebook_group ?? "" }
                            onChange={ handleChange }
                            placeholder="Enter Facebook group link"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Genre</Form.Label>
                        <Form.Control
                            name="genre"
                            value={ values.genre ?? "" }
                            onChange={ handleChange }
                            placeholder="Enter genre"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Post URL</Form.Label>
                        <Form.Control
                            name="post_url"
                            value={ values.post_url ?? "" }
                            onChange={ handleChange }
                            placeholder="Enter original post URL"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Tags</Form.Label>
                        <Form.Control
                            name="tags"
                            value={ values.tags ?? "" }
                            onChange={ handleChange }
                            placeholder="Enter tags"
                        />
                    </Form.Group>

                    {job && job.access_token &&
                        <Form.Group className="mb-3">
                            <Form.Label>Claim URL</Form.Label>
                            <Form.Control
                                name="claim_url"
                                value={ `${process.env.REACT_APP_WEBSITE_URL}/claim/${job.access_token}` }
                                readOnly
                            />
                        </Form.Group>
                    }

                    <Form.Group className="mb-3">
                        <Form.Label>Owner Reached</Form.Label>
                        <Form.Select
                            name="owner_reached"
                            value={ values.owner_reached ? "yes" : "no" }
                            onChange={(e) =>
                                setValues((old) => ({
                                    ...old,
                                    owner_reached: e.target.value === "yes"
                                }))
                            }
                        >
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                        </Form.Select>
                    </Form.Group>
                </Form>
            </Modal.Body>

            <Modal.Footer>
                { job && (
                    <Button className="ppy-btn ppy-btn-secondary" onClick={ handleDelete }>
                        Delete
                    </Button>
                ) }
                <Button className="ppy-btn ppy-btn-tertiary" onClick={ handleClose }>
                    Cancel
                </Button>
                <Button className="ppy-btn ppy-btn-primary" onClick={ handleSave }>
                    Save
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
